import { Injectable } from '@angular/core';
import { BaseService, Pagination } from '../core';
import {
  PurchaseOrderCreateDto,
  PurchaseOrder,
  PaypalCaptureDto,
  TTAttachmentDto,
  ResponseLatestBankAccountDto,
  Cart,
  ShippingTariffPrice,
  PurchaseOrderListDto,
} from '../dto';
import { Store } from '@ngrx/store';
import * as IndicatorStore from '../store/indicator';
import { mergeMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Utils } from '../common';

@Injectable({
  providedIn: 'root',
})
export class OrderService extends BaseService {
  static POINT_PERCENTAGE = 0.01; // 1%
  static POINT_LIMIT_PERCENT = 100; // %
  /**
   * 1000불 이상이면 TT만
   */
  static MAX_PG_PAYMENT_PRICE_USD = {
    // [PAYMENT_METHOD.PAYPAL]: 1000,
    // [PAYMENT_METHOD.EXIMBAY]: 2000,
  };

  constructor(private store: Store<any>) {
    super();
  }

  findInvalidCountries(cca2: string, carts: Cart[], region?: string) {
    return carts.filter((cart) => {
      return (
        cart.mapperSaleProduct.productOption.invalidCountry.includes(cca2) ||
        cart.mapperSaleProduct.productOption.brand.invalidCountry.includes(cca2) ||
        (region === 'Europe' && cart.mapperSaleProduct.productOption.brand.invalidEurope) ||
        (region !== 'Europe' && cart.mapperSaleProduct.productOption.brand.validOnlyEurope)
      );
    });
  }

  findAmazonBannedCountries(cca2: string, carts: Cart[]) {
    return carts.filter((cart) => {
      console.log(cca2, cart.mapperSaleProduct.productOption.brand.bannedAmazonCountry, cart.mapperSaleProduct.productOption.brand.allBannedAmazon);
      return (
        (cart.mapperSaleProduct.productOption.brand.bannedAmazon &&
        cart.mapperSaleProduct.productOption.brand.bannedAmazonCountry.includes(cca2)) ||
        cart.mapperSaleProduct.productOption.brand.allBannedAmazon
      );
    });
  }

  caculateCartsTotal(carts: Cart[]) {
    return carts.reduce(
      (acc, cur) => {
        acc.summary = Utils.getPointToFixed(
          Number(acc.summary + cur.mapperSaleProduct.productOption.salePriceUSD * cur.quantity)
        );
        acc.quantity = acc.quantity + cur.quantity;
        return acc;
      },
      { summary: 0, quantity: 0 }
    );
  }

  caculatePurchasePrice(carts: Cart[], shippingTariffPrice?: ShippingTariffPrice) {
    const total = this.caculateCartsTotal(carts);

    return {
      incoterms: shippingTariffPrice ? shippingTariffPrice.shippingService.incoterms : null,
      shippingServiceId: shippingTariffPrice ? shippingTariffPrice.shippingServiceId : null,
      shippingFee: shippingTariffPrice ? Utils.getPointToFixed(shippingTariffPrice.price) : 0,

      itemPrice: Utils.getPointToFixed(total.summary),
      purchasePrice: Utils.getPointToFixed(total.summary + (shippingTariffPrice ? shippingTariffPrice.price : 0)),
      paymentPrice: Utils.getPointToFixed(total.summary + (shippingTariffPrice ? shippingTariffPrice.price : 0)),
    };
  }

  requestPurchaseOrder(purchaseOrderCreateDto: PurchaseOrderCreateDto) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('requestPurchaseOrder'));
    return super.post<PurchaseOrder>('/purchase-order', purchaseOrderCreateDto).pipe(
      mergeMap((purchaseOrder) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('requestPurchaseOrder'));
        return of(purchaseOrder);
      })
    );
  }

  paypalCaptureAuthorization(paypalCaptureDto: PaypalCaptureDto) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('paypalCaptureAuthorization'));
    return super.post<PurchaseOrder>('/pg/payment/request', paypalCaptureDto).pipe(
      mergeMap((purchaseOrder) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('paypalCaptureAuthorization'));
        return of(purchaseOrder);
      })
    );
  }

  getPurchaseOrderList(purchaseOrderListDto: PurchaseOrderListDto, pagination: Pagination) {
    return super.paginate<PurchaseOrder>('/purchase-order', purchaseOrderListDto, pagination);
  }

  getMyMembershipList() {
    return super.get<any>('/my-membership-list');
  }

  getPurchaseOrderDetail(orderNumber: number) {
    return super.get<PurchaseOrder>(`/purchase-order/${orderNumber}`);
  }

  addToCart(orderNumber: number) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('historyToCart'));
    return super.post<Cart[]>(`/purchase-order/${orderNumber}/add-to-cart`).pipe(
      mergeMap((carts) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('historyToCart'));
        return of(carts);
      })
    );
  }

  cancelPurchaseOrder(orderNumber: number) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('cancelPurchaseOrder'));
    return super.put<PurchaseOrder>(`/purchase-order/${orderNumber}/cancel`).pipe(
      mergeMap((purchaseOrder) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('cancelPurchaseOrder'));
        return of(purchaseOrder);
      })
    );
  }

  completePurchaseOrder(orderNumber: number) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('completePurchaseOrder'));
    return super.put<PurchaseOrder>(`/purchase-order/${orderNumber}/completed`).pipe(
      mergeMap((purchaseOrder) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('completePurchaseOrder'));
        return of(purchaseOrder);
      })
    );
  }

  purchaseOrderAttachment(orderNumber: number, attachment: TTAttachmentDto) {
    this.store.dispatch(new IndicatorStore.ShowIndicator('purchaseOrderAttachment'));
    return super.put<PurchaseOrder>(`/purchase-order/${orderNumber}/tt-attachment`, attachment).pipe(
      mergeMap((purchaseOrder) => {
        this.store.dispatch(new IndicatorStore.HideIndicator('purchaseOrderAttachment'));
        return of(purchaseOrder);
      })
    );
  }

  downloadInvoice(orderNumber: number) {
    return super.download(`/file-download/pdf/invoice/${orderNumber}`);
  }

  downloadCoa(orderNumber: number) {
    return super.download(`/file-download/pdf/coa/${orderNumber}`);
  }

  getSavedBankAccount() {
    return super.get<ResponseLatestBankAccountDto>('/purchase-order/latest-bank-account');
  }

  maxUseablePoint(purchasePrice: number) {
    return Utils.getPointToFixed((purchasePrice * OrderService.POINT_LIMIT_PERCENT) / 100);
  }
}
